import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";

const WS_URL =
	process.env.REACT_APP_JOTA_WS_URL || "ws://localhost:3333/api/v1/jota/chat";
const RECONNECT_INTERVAL = 3000;
const MAX_RECONNECT_ATTEMPTS = 5;
const INACTIVITY_TIMEOUT = 30 * 60 * 1000;
const HEARTBEAT_INTERVAL = 30000;

const useJotaWebSocket = (onMessage, isOpen, onReset, onError) => {
	const [connectionStatus, setConnectionStatus] = useState("disconnected");
	const [sessionId, setSessionId] = useState(null);
	const [connectionId, setConnectionId] = useState(null);
	const [isSessionReady, setIsSessionReady] = useState(false);

	const wsRef = useRef(null);
	const reconnectTimeoutRef = useRef(null);
	const reconnectAttemptsRef = useRef(0);
	const sessionIdRef = useRef(null);
	const connectionIdRef = useRef(null);
	const isSessionActiveRef = useRef(false);
	const inactivityTimerRef = useRef(null);
	const heartbeatIntervalRef = useRef(null);
	const pendingSessionResolveRef = useRef(null);
	const token = useSelector(state => state.login.token) || "";

	const resetInactivityTimer = useCallback(() => {
		if (inactivityTimerRef.current) {
			clearTimeout(inactivityTimerRef.current);
		}
		inactivityTimerRef.current = setTimeout(() => {
			console.log(
				"[JOTA WebSocket] ⏱️ Timeout de 30 minutos - encerrando sessão"
			);
			if (onReset) {
				onReset();
			}
		}, INACTIVITY_TIMEOUT);
	}, [onReset]);

	const connect = useCallback(() => {
		if (!isOpen || !token) {
			console.log(
				"[JOTA WebSocket] ⚠️ Não conectando - isOpen:",
				isOpen,
				"| token:",
				!!token
			);
			return;
		}

		try {
			const wsUrl = `${WS_URL}?authorization=${encodeURIComponent(token)}`;
			console.log("[JOTA WebSocket] Tentando conectar em:", wsUrl);
			console.log("[JOTA WebSocket] Token presente:", !!token);
			const ws = new WebSocket(wsUrl);

			ws.onopen = () => {
				console.log("[JOTA WebSocket] ✅ Conectado com sucesso");
				setConnectionStatus("connected");
				reconnectAttemptsRef.current = 0;

				if (heartbeatIntervalRef.current) {
					clearInterval(heartbeatIntervalRef.current);
				}
				heartbeatIntervalRef.current = setInterval(() => {
					if (ws.readyState === WebSocket.OPEN) {
						try {
							ws.send(JSON.stringify({ type: "ping" }));
							console.log("[JOTA WebSocket] 💓 Heartbeat enviado");
						} catch (err) {
							console.error("[JOTA WebSocket] Erro ao enviar heartbeat:", err);
						}
					}
				}, HEARTBEAT_INTERVAL);

				if (!isSessionActiveRef.current || !sessionIdRef.current) {
					const startMessage = {
						type: "session/start",
						payload: { context: "JOTA_JURIDICO" }
					};
					console.log("[JOTA WebSocket] Enviando session/start:", startMessage);
					ws.send(JSON.stringify(startMessage));
				} else {
					console.log("[JOTA WebSocket] Sessão já ativa, reutilizando:", {
						sessionId: sessionIdRef.current,
						connectionId: connectionIdRef.current
					});
				}
			};

			ws.onmessage = event => {
				try {
					const data = JSON.parse(event.data);
					console.log("[JOTA WebSocket] Mensagem recebida:", data);

					if (data.sessionId && data.connectionId && !data.message) {
						const sid = Number(data.sessionId);
						const cid = String(data.connectionId);
						sessionIdRef.current = sid;
						connectionIdRef.current = cid;
						isSessionActiveRef.current = true;
						setSessionId(sid);
						setConnectionId(cid);
						setIsSessionReady(true);
						console.log("[JOTA WebSocket] Sessão iniciada:", {
							sessionId: sid,
							connectionId: cid
						});
						if (pendingSessionResolveRef.current) {
							pendingSessionResolveRef.current({
								sessionId: sid,
								connectionId: cid
							});
							pendingSessionResolveRef.current = null;
						}
					} else if (data.type === "session/started") {
						const sid = Number(data.sessionId);
						const cid = String(data.connectionId);
						sessionIdRef.current = sid;
						connectionIdRef.current = cid;
						isSessionActiveRef.current = true;
						setSessionId(sid);
						setConnectionId(cid);
						setIsSessionReady(true);
						console.log("[JOTA WebSocket] Sessão iniciada:", {
							sessionId: sid,
							connectionId: cid
						});
						if (pendingSessionResolveRef.current) {
							pendingSessionResolveRef.current({
								sessionId: sid,
								connectionId: cid
							});
							pendingSessionResolveRef.current = null;
						}
					} else if (
						data.type === "message" ||
						data.type === "text" ||
						data.type === "link"
					) {
						resetInactivityTimer();
						onMessage(data);
					} else if (data.type === "error") {
						console.error("[JOTA WebSocket] Erro:", data.message);
						onError?.();
					}
				} catch (err) {
					console.error("[JOTA WebSocket] Erro ao processar mensagem:", err);
				}
			};

			ws.onerror = error => {
				console.error("[JOTA WebSocket] ❌ Erro na conexão:", error);
				console.error("[JOTA WebSocket] URL tentada:", WS_URL);
				console.error("[JOTA WebSocket] ReadyState:", ws.readyState);
				setConnectionStatus("error");
			};

			ws.onclose = event => {
				console.log("[JOTA WebSocket] ⚠️ Conexão fechada");
				console.log("[JOTA WebSocket] Código:", event.code);
				console.log(
					"[JOTA WebSocket] Razão:",
					event.reason || "Nenhuma razão fornecida"
				);
				console.log("[JOTA WebSocket] Clean:", event.wasClean);
				setConnectionStatus("disconnected");
				wsRef.current = null;

				if (heartbeatIntervalRef.current) {
					clearInterval(heartbeatIntervalRef.current);
					heartbeatIntervalRef.current = null;
				}

				if (event.code === 1000) {
					sessionIdRef.current = null;
					connectionIdRef.current = null;
					isSessionActiveRef.current = false;
					setSessionId(null);
					setConnectionId(null);
					setIsSessionReady(false);
				}

				if (isOpen && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
					reconnectAttemptsRef.current += 1;
					console.log(
						`[JOTA WebSocket] Tentando reconectar (${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})...`
					);
					reconnectTimeoutRef.current = setTimeout(() => {
						connect();
					}, RECONNECT_INTERVAL);
				}
			};

			wsRef.current = ws;
		} catch (error) {
			console.error("[JOTA WebSocket] Erro ao criar conexão:", error);
			setConnectionStatus("error");
		}
	}, [isOpen, token, onMessage, onError, resetInactivityTimer]);

	useEffect(() => {
		if (isOpen) {
			setConnectionStatus("connecting");
			connect();
		} else {
			if (wsRef.current) {
				if (sessionIdRef.current) {
					try {
						wsRef.current.send(
							JSON.stringify({
								type: "session/end",
								payload: { sessionId: sessionIdRef.current }
							})
						);
					} catch (err) {
						console.error("[JOTA WebSocket] Erro ao encerrar sessão:", err);
					}
				}
				wsRef.current.close();
				wsRef.current = null;
			}
			if (reconnectTimeoutRef.current) {
				clearTimeout(reconnectTimeoutRef.current);
			}
			if (inactivityTimerRef.current) {
				clearTimeout(inactivityTimerRef.current);
				inactivityTimerRef.current = null;
			}
			if (heartbeatIntervalRef.current) {
				clearInterval(heartbeatIntervalRef.current);
				heartbeatIntervalRef.current = null;
			}
			setConnectionStatus("disconnected");
			sessionIdRef.current = null;
			connectionIdRef.current = null;
			isSessionActiveRef.current = false;
			setSessionId(null);
			setConnectionId(null);
			setIsSessionReady(false);
		}

		return () => {
			if (wsRef.current) {
				wsRef.current.close();
			}
			if (reconnectTimeoutRef.current) {
				clearTimeout(reconnectTimeoutRef.current);
			}
			if (inactivityTimerRef.current) {
				clearTimeout(inactivityTimerRef.current);
			}
			if (heartbeatIntervalRef.current) {
				clearInterval(heartbeatIntervalRef.current);
			}
		};
	}, [isOpen, connect]);

	const sendMessage = useCallback(
		async question => {
			if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
				console.error("[JOTA WebSocket] WebSocket não está conectado");
				return false;
			}

			if (
				!sessionIdRef.current ||
				!connectionIdRef.current ||
				!isSessionActiveRef.current
			) {
				console.log("[JOTA WebSocket] Aguardando sessão ser criada...");
				try {
					await new Promise((resolve, reject) => {
						pendingSessionResolveRef.current = resolve;
						setTimeout(() => {
							if (pendingSessionResolveRef.current) {
								pendingSessionResolveRef.current = null;
								reject(new Error("Timeout aguardando sessão"));
							}
						}, 5000);
					});
					console.log("[JOTA WebSocket] Sessão criada, enviando mensagem");
				} catch (error) {
					console.error("[JOTA WebSocket] Timeout aguardando sessão:", error);
					return false;
				}
			}

			try {
				const message = {
					type: "send",
					payload: {
						question,
						sessionId: sessionIdRef.current,
						connectionId: connectionIdRef.current
					}
				};

				console.log("[JOTA WebSocket] Enviando mensagem:", message);
				wsRef.current.send(JSON.stringify(message));
				resetInactivityTimer();
				return true;
			} catch (error) {
				console.error("[JOTA WebSocket] Erro ao enviar mensagem:", error);
				onError?.();
				return false;
			}
		},
		[onError, resetInactivityTimer]
	);

	const sendFeedback = useCallback(async (messageId, feedback) => {
		if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
			console.error("[JOTA WebSocket] WebSocket não está conectado");
			return false;
		}

		if (!connectionIdRef.current || !feedback) {
			console.error("[JOTA WebSocket] ConnectionId não disponível");
			return false;
		}

		const feedbackUpper =
			typeof feedback === "string"
				? feedback.toUpperCase()
				: String(feedback).toUpperCase();

		try {
			const message = {
				type: "feedback",
				payload: {
					messageId,
					feedback: feedbackUpper,
					connectionId: String(connectionIdRef.current)
				}
			};

			console.log("[JOTA WebSocket] Enviando feedback:", message);
			wsRef.current.send(JSON.stringify(message));
			return true;
		} catch (error) {
			console.error("[JOTA WebSocket] Erro ao enviar feedback:", error);
			return false;
		}
	}, []);

	const sendTransshipment = useCallback(async () => {
		if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
			console.error("[JOTA WebSocket] WebSocket não está conectado");
			return false;
		}

		if (sessionIdRef.current == null) {
			console.error("[JOTA WebSocket] SessionId não disponível");
			return false;
		}

		try {
			const message = {
				type: "transhipment",
				payload: {
					sessionId: Number(sessionIdRef.current)
				}
			};

			console.log("[JOTA WebSocket] Enviando transbordo:", message);
			wsRef.current.send(JSON.stringify(message));
			return true;
		} catch (error) {
			console.error("[JOTA WebSocket] Erro ao enviar transbordo:", error);
			return false;
		}
	}, []);

	const endSession = useCallback(() => {
		if (wsRef.current && sessionId) {
			try {
				wsRef.current.send(
					JSON.stringify({
						type: "session/end",
						payload: { sessionId }
					})
				);
			} catch (err) {
				console.error("[JOTA WebSocket] Erro ao encerrar sessão:", err);
			}
		}
	}, [sessionId]);

	return {
		connectionStatus,
		isSessionReady,
		sendMessage,
		sendFeedback,
		sendTransshipment,
		endSession
	};
};

export default useJotaWebSocket;
